import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { PublicRatingInfo } from '@goyone/shared';
import { api } from '../../lib/api';
import { longDay } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Logo } from '../../components/Logo';

/**
 * صفحة تقييم العميل — واجهة الشركة أمام عميلها.
 *
 * رابطٌ واحد الشكل لكل السياقات: مكالمة أو صفقة أو عقد أو مشروع أو
 * الخدمة عمومًا. نجومٌ كلّية، وعناصرُ تفصيلٍ إن ضبطتها الإدارة من
 * الإعدادات، وتعليقٌ إن شاء — والتقييم يُقبل مرة واحدة.
 *
 * `skipAuth` كصفحة الحجز وللسبب نفسه: الرمز في الرابط هو الهوية كلها.
 */

const SCORE_LABELS = ['', 'سيئة', 'دون المتوسط', 'مقبولة', 'جيدة', 'ممتازة'];

/** سؤال الصدر بحسب السياق — العميل يعرف ماذا يقيّم قبل أن يقرأ نجمة. */
const KIND_QUESTION: Record<string, string> = {
  CALL: 'كيف كانت مكالمتنا معك؟',
  ACCOUNT: 'كيف كانت تجربتك معنا؟',
  DEAL: 'كيف كانت تجربتك معنا في هذا التعاقد؟',
  CONTRACT: 'كيف كانت تجربتك في هذا التعاقد؟',
  PROJECT: 'كيف كانت تجربتك في هذا المشروع؟',
  TASK: 'ما رأيك في هذا العمل؟',
};

export function PublicRate() {
  const { token = '' } = useParams();
  const [score, setScore] = useState(0);
  const [hover, setHover] = useState(0);
  const [itemScores, setItemScores] = useState<Record<string, number>>({});
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const base = `/public/rate/${encodeURIComponent(token)}`;

  const info = useQuery({
    queryKey: ['rate', token],
    queryFn: () => api<PublicRatingInfo>(base, { skipAuth: true }),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () =>
      api(base, {
        method: 'POST',
        skipAuth: true,
        body: {
          score,
          scores: Object.keys(itemScores).length ? itemScores : undefined,
          comment: comment.trim() || undefined,
        },
      }),
    onSuccess: () => setDone(true),
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الإرسال.'),
  });

  /* ---------- الرابط غير صالح ---------- */
  if (info.isError) {
    return (
      <Frame>
        <div className="book-empty">
          <Icon name="star" size={32} />
          <h1 className="book-title">رابط التقييم غير متاح</h1>
          <p className="mute">الرابط غير صحيح أو لم يعد متاحًا. راجع من أرسله إليك.</p>
        </div>
      </Frame>
    );
  }

  if (info.isPending) {
    return (
      <Frame>
        <div className="skeleton" style={{ height: 220 }} />
      </Frame>
    );
  }

  /* ---------- قُيّمت من قبل أو أُرسلت الآن ---------- */
  if (info.data.submitted || done) {
    return (
      <Frame>
        <div className="book-done">
          <span className="book-check" aria-hidden="true">
            <Icon name="check" size={26} />
          </span>
          <h1 className="book-title">شكرًا لك</h1>
          <p className="mute">
            وصل تقييمك إلى {info.data.company} — رأيك يُحسّن الخدمة فعلًا.
          </p>
        </div>
      </Frame>
    );
  }

  const shown = hover || score;
  const d = info.data;

  return (
    <Frame>
      <header className="book-brand">
        <span className="book-logo book-logo-fallback">{d.company.slice(0, 2)}</span>
        <div>
          <div className="book-tenant">{d.company}</div>
          <h1 className="book-title">{KIND_QUESTION[d.kind] ?? KIND_QUESTION.ACCOUNT}</h1>
        </div>
      </header>

      <p className="mute small mt-2">
        {d.title ? (
          <>
            بخصوص: <strong>{d.title}</strong> —{' '}
          </>
        ) : d.kind === 'CALL' ? (
          <>مكالمة يوم {longDay(d.when)} — </>
        ) : null}
        تقييمك يصل للفريق مباشرةً.
      </p>

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {/* النجوم الكلّية. */}
      <div className="rate-stars" role="radiogroup" aria-label="التقييم من خمسة">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`rate-star${shown >= n ? ' on' : ''}`}
            onClick={() => setScore(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            role="radio"
            aria-checked={score === n}
            aria-label={`${n} من ٥ — ${SCORE_LABELS[n]}`}
          >
            <Icon name="star" size={34} />
          </button>
        ))}
      </div>
      <p className="rate-word small" aria-live="polite">
        {shown ? SCORE_LABELS[shown] : 'اختر عدد النجوم'}
      </p>

      {/* عناصر التفصيل — ما ضبطته إدارة الشركة لهذا السياق. */}
      {d.items.length > 0 && (
        <div className="rate-items">
          {d.items.map((item) => (
            <div key={item.id} className="rate-item">
              <span className="small">{item.label}</span>
              <span className="rate-item-stars" role="radiogroup" aria-label={item.label}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`rate-star sm${(itemScores[item.id] ?? 0) >= n ? ' on' : ''}`}
                    onClick={() => setItemScores((s) => ({ ...s, [item.id]: n }))}
                    role="radio"
                    aria-checked={itemScores[item.id] === n}
                    aria-label={`${n} من ٥`}
                  >
                    <Icon name="star" size={18} />
                  </button>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}

      {/*
        في الصفقات السبب هو المطلوب: نجومٌ بلا «لماذا» لا تُصلح بيعًا —
        وخاصةً حين نخسر. في البقية التعليق اختياري.
      */}
      <label className="field mt-3">
        <span className="label">
          {d.kind === 'DEAL' ? 'السبب — لماذا هذا التقييم؟' : 'أخبرنا أكثر — اختياري'}
        </span>
        <textarea
          className="textarea"
          rows={3}
          maxLength={1000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            d.kind === 'DEAL'
              ? 'بصراحة: ما الذي جعلك تُقدم أو تُحجم؟ السعر، التوقيت، بديلٌ آخر…'
              : 'ما أعجبك، وما تتمنى أن نحسّنه…'
          }
        />
      </label>

      <button
        className="btn btn-primary book-cta mt-3"
        disabled={score === 0 || (d.kind === 'DEAL' && !comment.trim()) || submit.isPending}
        onClick={() => submit.mutate()}
      >
        {submit.isPending ? 'جارٍ الإرسال…' : 'أرسل التقييم'}
      </button>
    </Frame>
  );
}

/** إطار الصفحة — كصفحة الحجز: بلا شريط ولا قائمة، فالزائر ليس مستخدمًا. */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="book-page">
      <div className="book-card">{children}</div>
      <p className="book-foot tiny mute">
        <Logo size={16} /> GoyOne
      </p>
    </div>
  );
}
